import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import BackendService from '../services/backendService';
import {
  NewPresentationMetadata,
  PromptInputData,
} from '../services/backendService/types';
import {
  GeneratorCoarseLicenseLevel,
  Template,
} from '../services/TemplateService/Template.type';
import { createBlankPresentation } from '../utils/generationUtils';
import { useMetrics } from './useMetrics';
import { presentationsCacheKey } from './usePresentations';
import { useRecentGeneratorsStorage } from './useRecentGenerators';
import useSendSlackMessage from './useSubmitFeedback';
import { templatesCacheKey } from './useTemplates';

const useConsumeGeneratorAsNewPresentation = (tenantId: string, isTestPrep?: boolean) => {
  const metrics = useMetrics();
  const client = useQueryClient();
  const { saveConsumeGenerator } = useRecentGeneratorsStorage();
  const { submit } = useSendSlackMessage();
  const [livePresentationId, setLivePresentationId] = useState<string | undefined>();

  const { mutate: consumeGenerator, isLoading, data, isError, error } = useMutation({
    mutationFn: async ({
      generatorId,
      variantId,
      generatorName,
      generatorPlan,
      userInputFormData,
      isRetry,
      metadata,
    }: {
      generatorId: string;
      variantId?: string;
      generatorName: string;
      generatorPlan?: GeneratorCoarseLicenseLevel | null | undefined;
      isRetry?: boolean;
      userInputFormData: PromptInputData[];
      onSuccessFunction?: (template: Template) => void;
      metadata?: NewPresentationMetadata;
    }) => {
      metrics.logEvent('Generator.Consume', {
        generatorId,
        generatorName,
        generatorPlan,
        userInputFormData,
        isRetry,
        isPresentation: true,
      });

      const blank = await createBlankPresentation(metadata);
      if (blank) {
        setLivePresentationId(blank.livePresentationId);
        client.invalidateQueries([templatesCacheKey]);
        client.invalidateQueries([presentationsCacheKey]);
      }

      const res = await BackendService.instance.ai.consumeGeneratorAsNewPresentation(
        {
          generatorId,
          generatorName,
          userInputFormData,
          ...(variantId ? { variantId } : {}),
          ...(metadata ? { metadata } : {}),
          ...(blank ? { targetPresentationId: blank.id } : {}),
        },
        tenantId,
      );
      return res;
    },
    onSuccess: (data, { onSuccessFunction, generatorId }) => {
      client.invalidateQueries(['hasReachedLimit']);
      saveConsumeGenerator(generatorId);
      if (onSuccessFunction && data) {
        onSuccessFunction(data);
      }
      return true;
    },
    cacheTime: 1000 * 60 * 5,
    onError: (
      error: any,
      { generatorId, generatorName }: { generatorId: string; generatorName?: string },
    ) => {
      submit({
        message: `\n*Error consuming ${
          isTestPrep ? 'test prep lesson' : 'customizable lesson'
        }:* ${
          error.customMessage
        }\n*Generator ID: ${generatorId}\n*Generator Name: ${generatorName}\n*Error message:* ${
          error.message
        }\n*Error status:* ${error.response?.status}\nError code: ${error.code}`,
        accepted: false,
      });
    },
  });

  return { consumeGenerator, isLoading, isError, data, error, livePresentationId };
};

export default useConsumeGeneratorAsNewPresentation;
